// Copyright (c) 2025, Dokos and Contributors
// MIT License. See license.txt

import "./PMTaskAssignmentQuickEntryForm";
import { PMViewCalendar_Plan } from "./PMViewCalendar_Plan";
import { PMViewCalendar_Timelog } from "./PMViewCalendar_Timelog";
import { PMViewGrid_Timelog } from "./PMViewGrid_Timelog";
import { PMViewMobile_Plan } from "./PMViewMobile_Plan";
import { getActivityTypeColors, h } from "./PMRender";
import { getCurrentEmployee, isMobileWidth } from "./PMUtils";

/** @typedef {"plan" | "log"} pm_mode_t */

/** @dokos: modes accepted from the URL (`?view=`). */
export const ALLOWED_MODES = /** @type {const} */ (["log", "plan"]);
/** @dokos: former modes, still accepted from old links (the Assign mode is part of Plan). */
const MODE_ALIASES = { assign: "plan" };
/** @dokos: calendar class of each mode. */
const CALENDARS = {
	plan: PMViewCalendar_Plan,
	log: PMViewCalendar_Timelog,
};
/** @dokos: layouts of the Log Time mode (user setting `pm_view.log_layout`). */
const LOG_LAYOUTS = { grid: PMViewGrid_Timelog, calendar: PMViewCalendar_Timelog };
/** @dokos: URL/route keys owned by the PM view, never list filters. */
const PM_ROUTE_KEYS = ["view", "date"];

const resolveMode = (v) => MODE_ALIASES[v] || v; // @dokos
const isValidMode = (v) => ALLOWED_MODES.includes(resolveMode(v)); // @dokos
const isValidDate = (v) =>
	typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && moment(v, "YYYY-MM-DD", true).isValid(); // @dokos

export class ProjectManagementView extends frappe.views.ListView {
	constructor(opts) {
		super(opts);
		this._mode = this.default_mode;
		this._date = "";
		this.pm_buttons = []; // @dokos: page buttons added by the current mode
		this.restore_pm_route_state(); // @dokos
	}

	/** @dokos: Plan from the Project list, Log Time from the other lists (backward compatible). */
	get default_mode() {
		return this.doctype === "Project" ? "plan" : "log";
	}

	get mode() {
		return this._mode;
	}
	set mode(v) {
		this._mode = isValidMode(v) ? resolveMode(v) : this.default_mode; // @dokos
		this.setActiveCalendar();
	}

	/** @dokos: "grid" (weekly grid) or "calendar" layout of the Log Time mode. */
	get log_layout() {
		const saved = frappe.get_user_settings(this.doctype, "pm_view")?.log_layout;
		const layout = this._log_layout || saved;
		return LOG_LAYOUTS[layout] ? layout : "grid";
	}
	set log_layout(layout) {
		if (!LOG_LAYOUTS[layout] || layout === this.log_layout) return;
		this._log_layout = layout;
		frappe.model.user_settings.save(this.doctype, "pm_view", { log_layout: layout });
		this.rebuildCalendar();
	}

	get date() {
		const dateObj = this.calendar?.fullcalendar?.getDate();
		const dateStr = dateObj && moment(dateObj).format("YYYY-MM-DD HH:mm:ss");
		return dateStr?.slice(0, 10) ?? this._date;
	}
	set date(v) {
		this._date = v;
		this.calendar?.fullcalendar?.gotoDate(v);
	}

	get_search_params() {
		const params = super.get_search_params();
		params.append("view", this.mode);
		// @dokos: don't write an empty `date=` (it can't be restored)
		const date = this.date;
		if (date) {
			params.append("date", date);
		}
		return params;
	}

	/**
	 * @dokos: read `view`/`date` from the URL (or `frappe.route_options`) and
	 * strip them so the list view doesn't turn them into filters.
	 * @returns {{mode?: string, date?: string}}
	 */
	pop_pm_route_state() {
		const state = {};
		const url = new URL(window.location.href);
		for (const key of PM_ROUTE_KEYS) {
			const value = url.searchParams.get(key) ?? frappe.route_options?.[key];
			if (value != null) {
				state[key === "view" ? "mode" : key] = value;
			}
			url.searchParams.delete(key);
			if (frappe.route_options) {
				delete frappe.route_options[key];
			}
		}
		if (url.href !== window.location.href) {
			history.replaceState(history.state, "", url.toString());
		}
		return state;
	}

	/** @dokos: apply the route state; before the first render only the backing fields are set. */
	restore_pm_route_state() {
		const { mode, date } = this.pop_pm_route_state();
		const new_mode = isValidMode(mode) ? resolveMode(mode) : null;
		const new_date = isValidDate(date) ? date : null;

		if (!this.calendar) {
			if (new_mode) this._mode = new_mode;
			if (new_date) this._date = new_date;
			return;
		}
		// Move the current calendar first: a mode switch rebuilds on its displayed date.
		if (new_date) this.date = new_date;
		if (new_mode && new_mode !== this.mode) this.mode = new_mode;
	}

	/** @dokos: `view`/`date` are PM view state, not filters. */
	parse_filters_from_route_options() {
		this.restore_pm_route_state();
		return super.parse_filters_from_route_options();
	}

	setup_defaults() {
		super.setup_defaults();
		this.page_title = __("Project Management");
	}

	setup_view() {
		// @dokos: warm the Log Time lookups (cached) so the first switch only waits for its week
		getCurrentEmployee();
		getActivityTypeColors();
		// Hide standard sort selectors that don't apply to calendar views
		if (this.sort_selector) {
			this.sort_selector.wrapper[0].classList.add("hidden");
		}
		this.setup_pm_header();
	}

	/** @dokos: the calendars load their own range: the list page-size buttons don't apply. */
	setup_paging_area() {
		super.setup_paging_area();
		// `hidden` is !important, it wins over the list's `$paging_area.toggle()`
		this.$paging_area.addClass("hidden");
	}

	/**
	 * @dokos: view header (Espresso): a toolbar with the mode switch, the period / layout
	 * switches and the phone legend button, then the legend as a quiet dk-legend row.
	 */
	setup_pm_header() {
		this.custom_header = h("div", { class: "pm-view-header" });
		const toolbar = h("div", { class: "pm-view-toolbar" });

		// @dokos: one es-tab-buttons per choice; `short` fits the mode switch on a phone (the
		// title keeps the full name as the accessible name there)
		const modes = [
			{ value: "plan", label: __("Plan", null, "PM View"), icon: "chart-gantt" },
			{
				value: "log",
				label: __("Log Time", null, "PM View"),
				short: __("Time", null, "PM View"),
				icon: "clock",
			},
		];
		this.modeSwitch = erpnext.dokos.ui.tab_buttons({
			label: __("Mode", null, "PM View"),
			value: this.mode,
			css_class: "pm-mode-switch",
			options: modes.map((m) => ({
				value: m.value,
				icon_left: m.icon,
				title: m.label,
				suffix: () =>
					h(
						"span",
						{},
						h("span", { class: m.short && "pm-label-full", text: m.label }),
						m.short &&
							h("span", {
								class: "pm-label-short",
								text: m.short,
								attrs: { "aria-hidden": "true" },
							})
					),
			})),
			on_change: (value) => value !== this.mode && (this.mode = value),
		});
		toolbar.appendChild(this.modeSwitch[0]);

		// @dokos: period switch (from the calendar) and Grid / Calendar switch (Log Time mode)
		this.switchesContainer = h("div", { class: "pm-view-switches" });
		toolbar.appendChild(this.switchesContainer);
		this.custom_header.appendChild(toolbar);

		this.legendContainer = h("div", {
			class: "pm-legend-container dk-legend",
			attrs: { role: "group", "aria-label": __("Legend") },
		});
		this.custom_header.appendChild(this.legendContainer);

		this.setup_legend_toggle(toolbar); // @dokos
		this.$result[0].parentNode.insertBefore(this.custom_header, this.$result[0]);
		this.observe_width(); // @dokos
	}

	/** @dokos: on a phone the legend is a popover opened by an "i" button (hidden on desktop). */
	setup_legend_toggle(toolbar) {
		this.legendContainer.id = `pm-legend-${frappe.utils.get_random(6)}`;
		const setOpen = (open) => {
			toggle.setAttribute("aria-expanded", open ? "true" : "false");
			this.legendContainer.classList.toggle("is-open", open);
		};
		const toggle = erpnext.dokos.ui.button({
			icon: "info",
			variant: "ghost",
			title: __("Legend"),
			css_class: "pm-legend-toggle",
			attrs: { "aria-expanded": "false", "aria-controls": this.legendContainer.id },
			onclick: () => setOpen(!this.legendContainer.classList.contains("is-open")),
		})[0];
		this.custom_header.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && this.legendContainer.classList.contains("is-open")) {
				setOpen(false);
				toggle.focus();
			}
		});
		document.addEventListener("click", (e) => {
			if (!this.custom_header.contains(e.target)) setOpen(false);
		});
		toolbar.appendChild(toggle);
	}

	/** @dokos: the Plan mode has a timeline and a phone layout, chosen from the width. */
	get plan_calendar() {
		return isMobileWidth(this.$result[0]) ? PMViewMobile_Plan : PMViewCalendar_Plan;
	}

	/** @dokos: rebuild the Plan mode when the width crosses the mobile breakpoint. */
	observe_width() {
		this.resizeObserver = new ResizeObserver(() => {
			if (this.mode !== "plan" || !this.calendar) return;
			if (this.calendar.constructor !== this.plan_calendar) this.rebuildCalendar();
		});
		this.resizeObserver.observe(this.$result[0]);
	}

	async render() {
		if (this.calendar) {
			this.calendar.refresh();
			return;
		}
		await this.setActiveCalendar();
	}

	async setActiveCalendar() {
		// @dokos: the mode can change from the URL too: keep the switch in sync (silently)
		this.modeSwitch?.data("es-tab-buttons")?.set_value(this.mode, { silent: true });

		await this.rebuildCalendar();
	}

	async rebuildCalendar() {
		this.update_url_with_filters();
		this._date = this.date; // @dokos: keep the displayed date when switching modes
		if (this.calendar?.destroy) {
			this.calendar.destroy();
		}

		this.legendContainer.replaceChildren();
		this.switchesContainer.replaceChildren(); // @dokos
		this.render_layout_switch(); // @dokos

		// @dokos: the Log Time mode has two layouts
		const CalendarClass =
			this.mode === "log"
				? LOG_LAYOUTS[this.log_layout]
				: this.mode === "plan"
				? this.plan_calendar // @dokos: timeline or phone layout
				: CALENDARS[this.mode] || PMViewCalendar_Timelog;

		this.calendar = new CalendarClass({
			page: this.page,
			view: this,
			parent: this.$result,
		});

		if (this.calendar.setupLegend) {
			this.calendar.setupLegend(this.legendContainer);
		}

		await this.calendar.make();

		// @dokos: the calendar's period switch (before the Grid / Calendar switch of Log Time)
		this.calendar.setupPeriodSwitch?.(this.switchesContainer);

		this.setup_pm_actions(); // @dokos
	}

	/** @dokos: Grid / Calendar switch (es-tab-buttons), only in the Log Time mode. */
	render_layout_switch() {
		if (this.mode !== "log") return;
		const layouts = [
			["grid", __("Grid", null, "PM View"), "table"],
			["calendar", __("Calendar"), "calendar"],
		];
		this.switchesContainer.appendChild(
			erpnext.dokos.ui.tab_buttons({
				label: __("Layout", null, "PM View"),
				value: this.log_layout,
				css_class: "pm-layout-switch",
				// @dokos: the icon alone on a phone (the title stays the accessible name)
				options: layouts.map(([value, label, iconName]) => ({
					value,
					icon_left: iconName,
					title: label,
					suffix: () => h("span", { class: "pm-layout-label", text: label }),
				})),
				on_change: (layout) => (this.log_layout = layout),
			})[0]
		);
	}

	/** @dokos: an inner page button owned by the current mode (removed on mode change). */
	add_pm_button(label, action) {
		const button = this.page.add_inner_button(label, action);
		if (button) this.pm_buttons.push({ label, button });
	}

	/** @dokos: remove the mode's inner buttons and their mobile menu items. */
	clear_pm_buttons() {
		for (const { label, button } of this.pm_buttons) {
			button.remove();
			this.page.menu
				?.find(`.menu-item-label[data-label="${encodeURIComponent(label)}"]`)
				.closest("li")
				.remove();
		}
		this.pm_buttons = [];
	}

	/** @dokos: primary (and secondary) page actions of the current mode. */
	setup_pm_actions() {
		this.page.clear_secondary_action();
		this.clear_pm_buttons();
		if (this.mode === "log" && this.calendar?.getPageActions) {
			// weekly grid: Add Entry + Fill from assignments / Submit / Cancel Timesheet
			this.page.set_primary_action(__("Add Entry"), () => this.calendar.openTimeLogDialog());
			for (const { label, action } of this.calendar.getPageActions()) {
				this.add_pm_button(label, action);
			}
			return;
		}
		if (this.mode === "plan") {
			this.page.set_primary_action(__("Add Assignment"), () => this.calendar.openAssignment({}));
			if (frappe.model.can_create("Project")) {
				this.page.set_secondary_action(
					__("Project"),
					() => this.calendar.openProjectQuickEntry(),
					"plus"
				);
			}
			return;
		}
		this.page.set_primary_action(__("Add Entry"), () => {
			this.calendar.eventSources[0].doGuiCreateDocument();
		});
	}
}

frappe.views.registerCustomListView?.(
	"Project Management",
	"pm",
	"chart-gantt", // @dokos: lucide sprite (es-line-* icons no longer exist)
	ProjectManagementView,
	(listViewSelect) => {
		return {
			label: "Project Management",
			condition: ["Project", "Task", "Task Assignment", "Timesheet"].includes(listViewSelect.doctype),
			action: () => listViewSelect.set_route("pm"),
			current_view_handler: () => {},
		};
	}
);
